import { SALES_AGENT_CLAIM_TTL_DAYS } from 'utils/roles';

export type LeadOwnership = {
  who?: string | null;
  userByWho?: { id?: string | null } | null;
  claimed_at?: string | null;
};

export function getLeadOwnerId(lead?: LeadOwnership | null) {
  return lead?.who || lead?.userByWho?.id || null;
}

export function isLeadUnassigned(lead?: LeadOwnership | null) {
  return !getLeadOwnerId(lead);
}

export function isLeadOwnedBy(
  lead: LeadOwnership | null | undefined,
  userId: string,
) {
  if (!userId) return false;
  return getLeadOwnerId(lead) === userId;
}

export function matchesLeadOwnershipFilter(
  lead: LeadOwnership,
  filter: 'all' | 'pool' | 'mine',
  userId: string,
) {
  if (filter === 'pool') {
    return isLeadUnassigned(lead);
  }
  if (filter === 'mine') {
    return isLeadOwnedBy(lead, userId);
  }
  return true;
}

export function matchesLeadAssigneeFilter(lead: LeadOwnership, filter: string) {
  if (!filter || filter === 'all') {
    return true;
  }
  if (filter === 'unassigned') {
    return isLeadUnassigned(lead);
  }
  return getLeadOwnerId(lead) === filter;
}

/** Mask contact for unclaimed pool cards when the viewer is a sales agent. */
export function shouldMaskLeadContact(
  lead: LeadOwnership,
  opts: { isSalesAgent: boolean; userId: string },
) {
  if (!opts.isSalesAgent) return false;
  if (isLeadOwnedBy(lead, opts.userId)) return false;
  return isLeadUnassigned(lead);
}

export function maskContactValue(value?: string | null) {
  if (!value) return '—';
  if (value.includes('@')) {
    const [local, domain] = value.split('@');
    return `${local.slice(0, 1)}•••@${domain || '…'}`;
  }
  const digits = value.replace(/\D/g, '');
  if (digits.length < 4) return '••••';
  return `••••${digits.slice(-4)}`;
}

export function isClaimExpired(
  lead: LeadOwnership,
  now = new Date(),
  ttlDays = SALES_AGENT_CLAIM_TTL_DAYS,
) {
  if (!lead.claimed_at || isLeadUnassigned(lead)) return false;
  const claimed = new Date(lead.claimed_at).getTime();
  if (Number.isNaN(claimed)) return false;
  const ageMs = now.getTime() - claimed;
  return ageMs > ttlDays * 24 * 60 * 60 * 1000;
}
